import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  Paper,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Message as MessageIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  AutoFixHigh as AutoFixHighIcon,
  ChevronLeft,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api, externalApi } from '../../../services/authService';
import ConversationThreadPanel from '../../../components/ConversationThreadPanel';


// Helper function to get entity name by ID
const getEntityNameById = (entities, id) => {
  const entity = entities.find((e) => e.id === id);
  return entity ? entity.name : "Unknown";
};

// Helper function to truncate text to approximately 15 words
const truncateText = (text, wordLimit = 15) => {
  const words = text.split(" ");
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "...";
};

const ConversationsInbox = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedConversationLead, setSelectedConversationLead] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [showConversationDetails, setShowConversationDetails] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [channelAnchorEl, setChannelAnchorEl] = useState(null);
  const [conversationLeads, setConversationLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [threadMessages, setThreadMessages] = useState([]);
  const [leadThreads, setLeadThreads] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState('');
  const [selectedFunnelStep, setSelectedFunnelStep] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchCampaigns(selectedAccount);
    } else {
      setCampaigns([]);
      setSelectedCampaign('');
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (selectedAccount && selectedCampaign) {
      fetchFunnels(selectedCampaign);
    } else {
      setFunnels([]);
      setSelectedFunnel('');
    }
  }, [selectedAccount, selectedCampaign]);

  useEffect(() => {
    if (selectedAccount && selectedCampaign) {
      fetchConversationLeads();
    } else {
      setConversationLeads([]);
      setSelectedConversationLead(null);
    }
  }, [selectedAccount, selectedCampaign]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/crm/accounts/');
      setAccounts(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedAccount(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    }
  };

  const fetchCampaigns = async (accountId) => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/campaigns/`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    }
  };

  const fetchFunnels = async (campaignId) => {
    try {
      const response = await api.get(`/crm/campaigns/${campaignId}/funnels/`);
      setFunnels(response.data || []);
    } catch (error) {
      console.error('Error fetching funnels:', error);
      setFunnels([]);
    }
  };

  const fetchConversationLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/communications/conversation-threads/leads/', {
        params: {
          campaign_id: selectedCampaign,
          account_id: selectedAccount,
          funnel_id: selectedFunnel
        }
      });
      setConversationLeads(response.data || []);
      console.log('Current conversation leads:', response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching conversation leads:', error);
      setConversationLeads([]);
      setLoading(false);
    }
  };

  const fetchThreadMessages = async (threadId) => {
    try {
      setLoading(true);
      const response = await api.get('/communications/thread-messages/', {
        params: {
          thread_id: threadId
        }
      });
      setThreadMessages(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching thread messages:', error);
      setThreadMessages([]);
      setLoading(false);
    }
  };

  const fetchLeadThreads = async (leadId) => {
    try {
      setLoading(true);
      const response = await api.get('/communications/conversation-threads/', {
        params: {
          lead_id: leadId,
          account_id: selectedAccount,
          campaign_id: selectedCampaign
        }
      });
      setLeadThreads(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching lead threads:', error);
      setLeadThreads([]);
      setLoading(false);
    }
  };

  const handleConversationSelect = async (conversation) => {
    setSelectedConversationLead(conversation);
    setSelectedThread(null);
    setThreadMessages([]);
    if (!leadThreads.some(thread => thread.lead_id === conversation.id)) {
      await fetchLeadThreads(conversation.id);
    }
  };

  const handleThreadSelect = async (threadId) => {
    setSelectedThread(threadId);
    setShowConversationDetails(true);
    await fetchThreadMessages(threadId);
  };

  const handleBackToThreads = () => {
    setShowConversationDetails(false);
    setSelectedThread(null);
    setThreadMessages([]);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    
    try {
      await api.post(`/communications/conversation-threads/${selectedThread}/send/`, {
        sender_type: 'user',
        content: replyText,
        channel: selectedConversationLead.channel,
      });
      
      // Re-fetch the thread messages to show the new message
      await fetchThreadMessages(selectedThread);
      setReplyText("");
    } catch (error) {
      // Error handling without console.log
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "success";
      case "assigned":
        return "primary";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const ChannelIcon = ({ channel }) => {
    switch (channel) {
      case "sms":
        return <MessageIcon />;
      case "email":
        return <MailIcon />;
      case "voice":
        return <PhoneIcon />;
      case "chat":
        return <ChatIcon />;
      default:
        return <MessageIcon />;
    }
  };

  const filteredConversations = conversationLeads.filter((lead) => {
    const matchesSearch =
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.last_message?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesChannel = selectedChannel ? lead.latest_thread?.channel === selectedChannel : true;
    const matchesStatus = selectedStatus ? lead.latest_thread?.status === selectedStatus : true;

    return matchesSearch && matchesChannel && matchesStatus;
  });

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleChannelClick = (event) => {
    setChannelAnchorEl(event.currentTarget);
  };

  const handleChannelClose = () => {
    setChannelAnchorEl(null);
  };

  const getThreadsForContact = (contactId) => {
    return conversationLeads.filter((lead) => lead.id === contactId);
  };

  const getLeadInitials = (lead) => {
    if (!lead) return 'N/A';
    const firstName = lead.first_name || '';
    const lastName = lead.last_name || '';
    const name = `${firstName} ${lastName}`.trim();
    return name ? getInitials(name) : 'N/A';
  };

  const getLeadName = (lead) => {
    if (!lead) return 'Unknown';
    const firstName = lead.first_name || '';
    const lastName = lead.last_name || '';
    const name = `${firstName} ${lastName}`.trim();
    return name || 'Unknown';
  };

  const getLeadNameWithPhone = (lead) => {
    const name = getLeadName(lead);
    const phone = lead.phone_number ? ` • ${lead.phone_number}` : '';
    return `${name}${phone}`;
  };

  const handleThreadCreated = () => {
    if (selectedConversationLead) {
      fetchLeadThreads(selectedConversationLead.id);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Account and Campaign Selection */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="account-select-label">Select Account</InputLabel>
              <Select
                labelId="account-select-label"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                label="Select Account"
              >
                {Array.isArray(accounts) && accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="campaign-select-label">Select Campaign</InputLabel>
              <Select
                labelId="campaign-select-label"
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                label="Select Campaign"
                disabled={!selectedAccount}
              >
                {Array.isArray(campaigns) && campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="funnel-select-label">Select Funnel</InputLabel>
              <Select
                labelId="funnel-select-label"
                value={selectedFunnel}
                onChange={(e) => setSelectedFunnel(e.target.value)}
                label="Select Funnel"
                disabled={!selectedCampaign}
              >
                <MenuItem value="">All Funnels</MenuItem>
                {Array.isArray(funnels) && funnels.map((funnel) => (
                  <MenuItem key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Left panel - Conversation list */}
        <Paper sx={{ width: '30%', mr: 2, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                size="small"
                onClick={handleFilterClick}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleFilterClose}
              >
                <MenuItem disabled>Filter by Channel</MenuItem>
                <Divider />
                <MenuItem onClick={() => { setSelectedChannel(null); handleFilterClose(); }}>
                  All Channels
                </MenuItem>
                <MenuItem onClick={() => { setSelectedChannel("sms"); handleFilterClose(); }}>
                  <MessageIcon sx={{ mr: 1 }} /> SMS
                </MenuItem>
                <MenuItem onClick={() => { setSelectedChannel("email"); handleFilterClose(); }}>
                  <MailIcon sx={{ mr: 1 }} /> Email
                </MenuItem>
                <MenuItem onClick={() => { setSelectedChannel("voice"); handleFilterClose(); }}>
                  <PhoneIcon sx={{ mr: 1 }} /> Voice
                </MenuItem>
                <MenuItem onClick={() => { setSelectedChannel("chat"); handleFilterClose(); }}>
                  <ChatIcon sx={{ mr: 1 }} /> Chat
                </MenuItem>
                <Divider />
                <MenuItem disabled>Filter by Status</MenuItem>
                <Divider />
                <MenuItem onClick={() => { setSelectedStatus(null); handleFilterClose(); }}>
                  All Statuses
                </MenuItem>
                <MenuItem onClick={() => { setSelectedStatus("open"); handleFilterClose(); }}>
                  Open
                </MenuItem>
                <MenuItem onClick={() => { setSelectedStatus("assigned"); handleFilterClose(); }}>
                  Assigned
                </MenuItem>
                <MenuItem onClick={() => { setSelectedStatus("closed"); handleFilterClose(); }}>
                  Closed
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          <List sx={{ flexGrow: 1, overflow: 'auto' }}>
            {!selectedAccount || !selectedCampaign ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Please select an account and campaign to view conversations
                </Typography>
              </Box>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : conversationLeads.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No conversations found for the selected filters
                </Typography>
              </Box>
            ) : (
              conversationLeads.map((lead) => (
                <React.Fragment key={lead.id}>
                  <ListItem
                    button
                    selected={selectedConversationLead?.id === lead.id}
                    onClick={() => handleConversationSelect(lead)}
                  >
                    <ListItemAvatar>
                      <Avatar>{getLeadInitials(lead)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getLeadNameWithPhone(lead)}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {lead.last_message}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Badge
                        color={getStatusColor(lead.latest_thread?.status)}
                        variant="dot"
                        sx={{ mr: 1 }}
                      />
                      <ChannelIcon channel={lead.latest_thread?.channel} />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>

        {/* Right panel - Conversation threads or details */}
        <ConversationThreadPanel
          selectedConversation={selectedConversationLead}
          onBack={handleBackToThreads}
          onSendMessage={handleSendReply}
          showDetails={showConversationDetails}
          threads={leadThreads}
          onThreadSelect={handleThreadSelect}
          messages={threadMessages}
          selectedThread={selectedThread}
          onThreadCreated={handleThreadCreated}
          onAssign={(conversation) => {
            // TODO: Implement assign functionality
          }}
          onMarkResolved={(conversation) => {
            // TODO: Implement mark as resolved functionality
          }}
          onSnooze={(conversation) => {
            // TODO: Implement snooze functionality
          }}
          onViewContact={(conversation) => {
            // TODO: Implement view contact functionality
          }}
          onAddNote={(conversation) => {
            // TODO: Implement add note functionality
          }}
          onAddToCampaign={(conversation) => {
            // TODO: Implement add to campaign functionality
          }}
          onDeleteConversation={(conversation) => {
            // TODO: Implement delete conversation functionality
          }}
        />
      </Box>
    </Box>
  );
};

export default ConversationsInbox; 